import {
	ReturnOderLabelResponse,
	ReturnToOmsPayload,
	ReturnToOmsResponse,
	SapInstoreStockResponse,
} from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class ReturnOrder<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description return order
	 *
	 * @tags ReturnOrder
	 * @name ReturnOrderAddressCheck
	 * @summary return order
	 * @request GET:/store/{storeId}/returns/validate_order_by_orderid_zipcode_housenumber/{orderId}/{zipcode}/{houseNumber}
	 * @secure
	 * @response `200` `any` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	returnOrderAddressCheck = (
		storeId: string,
		orderId: string,
		zipcode: string,
		houseNumber: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** orderNumber. */
			orderNumber?: string;
			/** postCode */
			postCode?: string;
			/** houseNumber */
			houseNumber?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['returnOrderAddressCheck'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/returns/validate_order_by_orderid_zipcode_housenumber/${orderId}/${zipcode}/${houseNumber}`,
				params: _params,
				query: query,
				methodName: 'returnOrderAddressCheck',
			};
			logger.trace(traceData);
		}
		return this.http.request<any, void>({
			path: `/store/${storeId}/returns/validate_order_by_orderid_zipcode_housenumber/${orderId}/${zipcode}/${houseNumber}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description return order
	 *
	 * @tags ReturnOrder
	 * @name ValidateRevokeOrder
	 * @summary return order
	 * @request GET:/store/{storeId}/returns/validate_revoke_order_by_orderid_zipcode_housenumber/{orderId}/{zipcode}/{houseNumber}
	 * @secure
	 * @response `200` `any` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	validateRevokeOrder = (
		storeId: string,
		orderId: string,
		zipcode: string,
		houseNumber: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** orderNumber. */
			orderNumber?: string;
			/** postCode */
			postCode?: string;
			/** houseNumber */
			houseNumber?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['validateRevokeOrder'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/returns/validate_revoke_order_by_orderid_zipcode_housenumber/${orderId}/${zipcode}/${houseNumber}`,
				params: _params,
				query: query,
				methodName: 'validateRevokeOrder',
			};
			logger.trace(traceData);
		}
		return this.http.request<any, void>({
			path: `/store/${storeId}/returns/validate_revoke_order_by_orderid_zipcode_housenumber/${orderId}/${zipcode}/${houseNumber}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description return order reasons
	 *
	 * @tags ReturnOrder
	 * @name ReturnOrderReasons
	 * @summary return order
	 * @request GET:/store/{storeId}/returns/reasons
	 * @secure
	 * @response `200` `SapInstoreStockResponse` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	returnOrderReasons = (
		storeId: string,
		query: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** langId. */
			langId: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['returnOrderReasons'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/returns/reasons`,
				params: _params,
				query: query,
				methodName: 'returnOrderReasons',
			};
			logger.trace(traceData);
		}
		return this.http.request<SapInstoreStockResponse, void>({
			path: `/store/${storeId}/returns/reasons`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description return order submit request to oms and fetch generated unique barcode for the returned order
	 *
	 * @tags ReturnOrder
	 * @name ReturnOrderSubmitToOms
	 * @summary return order
	 * @request POST:/store/{storeId}/returns/submit_returns_to_oms/{orderId}
	 * @secure
	 * @response `200` `ReturnToOmsResponse` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	returnOrderSubmitToOms = (
		storeId: string,
		orderId: string,
		data: ReturnToOmsPayload,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['returnOrderSubmitToOms'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/returns/submit_returns_to_oms/${orderId}`,
				params: _params,
				query: query,
				body: data,
				methodName: 'returnOrderSubmitToOms',
			};
			logger.trace(traceData);
		}
		return this.http.request<ReturnToOmsResponse, void>({
			path: `/store/${storeId}/returns/submit_returns_to_oms/${orderId}`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description return order download label
	 *
	 * @tags ReturnOrder
	 * @name ReturnOrderDownloadReturnLabel
	 * @summary return order
	 * @request GET:/store/{storeId}/returns/download/byOrderId/{orderId}/barcode/{barCode}
	 * @secure
	 * @response `200` `ReturnOderLabelResponse` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	returnOrderDownloadReturnLabel = (
		storeId: string,
		orderId: string,
		barCode: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['returnOrderDownloadReturnLabel'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/returns/download/byOrderId/${orderId}/barcode/${barCode}`,
				params: _params,
				query: query,
				methodName: 'returnOrderDownloadReturnLabel',
			};
			logger.trace(traceData);
		}
		return this.http.request<ReturnOderLabelResponse, void>({
			path: `/store/${storeId}/returns/download/byOrderId/${orderId}/barcode/${barCode}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
